<template>
  <v-container fluid class="modules-page">
    <div class="modules-container">
      <!-- Header -->
      <div class="modules-header mb-6">
        <div class="d-flex align-start justify-space-between">
          <div class="d-flex align-center">
            <div class="modules-header__logo mr-4">
              <img src="@/assets/lg_grupo.png" alt="Grupo Parque" />
            </div>
            <div class="modules-header__content">
              <h1 class="modules-header__title mb-1">Módulos do Sistema</h1>
              <p class="modules-header__greeting mb-1">
                Olá, {{ userName }}!
              </p>
              <p class="modules-header__subtitle mb-0">
                Selecione o módulo que deseja acessar
              </p>
            </div>
          </div>

          <!-- Botão de Logout -->
          <v-btn outlined @click="logout" class="logout-btn red-soft">
            <v-icon left small>mdi-logout</v-icon>
            Sair
          </v-btn>
        </div>
      </div>

      <!-- Busca -->
      <v-text-field
        v-model="search"
        placeholder="Buscar módulos..."
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        class="modules-search mb-6"
      />

      <!-- Grid de módulos -->
      <v-row>
        <v-col
          v-for="mod in filteredModules"
          :key="mod.id"
          cols="12"
          sm="6"
          md="3"
        >
          <v-card
            class="module-card"
            :class="{ 'module-card--disabled': mod.disabled }"
            :ripple="!mod.disabled"
            flat
            @click="!mod.disabled && selectModule(mod)"
          >
            <div
              class="module-card__accent"
              :style="{ backgroundColor: mod.color }"
            />

            <v-card-text class="module-card__body">
              <div class="d-flex align-start justify-space-between mb-4">
                <div
                  class="module-card__icon"
                  :style="{
                    backgroundColor: mod.color + '18',
                    color: mod.color,
                  }"
                >
                  <v-icon :color="mod.color" size="22">{{ mod.icon }}</v-icon>
                </div>
                <span
                  v-if="mod.isNew"
                  class="module-card__badge module-card__badge--new"
                >
                  NOVO
                </span>
              </div>

              <h3 class="module-card__title">{{ mod.name }}</h3>
              <p class="module-card__desc">{{ mod.description }}</p>

              <span v-if="mod.disabled" class="module-card__no-access">
                Sem permissão
              </span>
              <button
                v-else
                class="module-card__link"
                :style="{ color: mod.color }"
                @click.stop="selectModule(mod)"
              >
                Acessar módulo
                <v-icon :color="mod.color" size="14" class="ml-1"
                  >mdi-arrow-right</v-icon
                >
              </button>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { modules, getModuleItemCount, drawerItems } from "@/config/navigationConfig";

export default {
  name: "ModuleSelector",

  data: () => ({
    modules,
    drawerItems,
    search: "",
    userName: "",
  }),

  mounted() {
    // Limpa o módulo selecionado e fecha a sidebar ao abrir a tela de módulos
    this.$store.commit("clearSelectedModule");
    this.$store.commit("SET_DRAWER", false);
    
    const user = JSON.parse(sessionStorage.getItem("user"));
    this.userName = user?.nome || "Usuário";
  },

  computed: {
    availableModules() {
      const user = JSON.parse(sessionStorage.getItem("user"));
      const userRoles = user?.roles || [];
      const isAdmin = userRoles.includes("ROLE_ADMIN");
      const hasFinanceRole = userRoles.includes("ROLE_FINANCEIRO");

      return this.modules.map((module) => {
        const itemCount = getModuleItemCount(module.groups, userRoles);
        const hasRoleAccess = module.roles?.length
          ? module.roles.some((role) => userRoles.includes(role))
          : false;
        const hasAccess =
          module.openAccess
            ? true
            : module.id === "financial"
            ? isAdmin || hasFinanceRole
            : module.roles?.length
            ? hasRoleAccess
            : module.adminOnly
            ? isAdmin
            : isAdmin || itemCount > 0;

        return {
          ...module,
          disabled: !hasAccess,
        };
      });
    },

    filteredModules() {
      const term = this.search.toLowerCase().trim();
      if (!term) return this.availableModules;

      return this.availableModules.filter((mod) => {
        // Busca no nome e descrição do módulo
        const matchesModule = 
          mod.name.toLowerCase().includes(term) ||
          mod.description.toLowerCase().includes(term);

        // Busca nas palavras-chave cadastradas para o módulo
        const matchesKeyword = (mod.searchKeywords || []).some((keyword) =>
          keyword.toLowerCase().includes(term)
        );

        // Busca nos títulos dos itens de menu que pertencem aos grupos deste módulo
        const matchesMenuItem = this.hasMenuItemInModule(mod.groups, term);

        return matchesModule || matchesKeyword || matchesMenuItem;
      });
    },
  },

  methods: {
    hasMenuItemInModule(moduleGroups, searchTerm) {
      let currentGroup = null;

      // Percorre os itens do drawer
      for (const item of this.drawerItems) {
        // Se é um título de grupo (não tem ícone nem rota)
        if (item.title && !item.to && !item.icon) {
          currentGroup = item.title;
        } 
        // Se estamos dentro de um grupo que pertence ao módulo
        else if (currentGroup && moduleGroups.includes(currentGroup)) {
          // Verifica se o título do item corresponde à busca
          if (item.title && item.title.toLowerCase().includes(searchTerm)) {
            return true;
          }

          // Verifica também nos children (submenus)
          if (item.children && Array.isArray(item.children)) {
            for (const child of item.children) {
              if (child.title && child.title.toLowerCase().includes(searchTerm)) {
                return true;
              }
            }
          }
        }
      }

      return false;
    },

    selectModule(module) {
      if (module.disabled) return;

      this.$store.commit("setSelectedModule", {
        id: module.id,
        groups: module.groups,
        color: module.color,
        name: module.name,
        icon: module.icon,
      });

      if (module.externalRoute) {
        this.$router.push(module.externalRoute);
        return;
      }

      this.$router.push("/inicio");
    },

    logout() {
      sessionStorage.clear();
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.modules-page {
  min-height: 100vh;
  background: #f2f4f7;
  padding: 2rem 1.5rem;
}

.modules-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.modules-header__logo {
  width: 60px;
  flex-shrink: 0;
}

.modules-header__logo img {
  width: 100%;
  height: auto;
  display: block;
}

.modules-header__content {
  display: flex;
  flex-direction: column;
}

.modules-header__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  line-height: 1.2;
}

.modules-header__greeting {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #5a8cba;
  margin: 0;
  line-height: 1.4;
}

.modules-header__subtitle {
  font-size: 0.875rem;
  color: #8492a6;
  margin: 0;
  line-height: 1.4;
}

/* Logout Button */
.logout-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px !important;
}

.red-soft {
  color: #c62828; /* vermelho mais fechado */
  border-color: #c62828;
}

.red-soft:hover {
  background-color: rgba(198, 40, 40, 0.08);
}

/* Search */
.modules-search {
  max-width: 420px;
}

.modules-search >>> .v-input__slot {
  border-radius: 8px !important;
  background: #fff !important;
  min-height: 40px !important;
}

.modules-search >>> .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
}

.modules-search >>> .v-input--is-focused .v-input__slot fieldset {
  border-color: #5a8cba !important;
}

/* Card */
.module-card {
  border-radius: 10px !important;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
  height: 100%;
}

.module-card:hover:not(.module-card--disabled) {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.module-card--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  filter: grayscale(40%);
}

.module-card--disabled:hover {
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}

.module-card__accent {
  height: 4px;
  width: 100%;
}

.module-card__body {
  padding: 1.25rem 1.25rem 1rem !important;
}

.module-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.module-card__badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
}

.module-card__badge--new {
  color: #1b7f3a;
  background-color: #e7f6ec;
  letter-spacing: 0.04em;
}

.module-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.35rem;
}

.module-card__desc {
  font-size: 0.8125rem;
  color: #8492a6;
  margin: 0 0 1rem;
  line-height: 1.45;
}

.module-card__link {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.15s;
}

.module-card__link:hover {
  opacity: 0.75;
}

.module-card__no-access {
  font-size: 0.75rem;
  color: #8492a6;
  font-style: italic;
}
</style>
